import { computed, inject, Injectable, signal } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { DateTime } from 'luxon';

import { CapabilitiesStore, TCapabilitiesFiltersStore } from '@core/stores';
import { NotifyService } from '@core/shared/services';
import { DateTimeAdapter } from '@core/domain/adapters';
import { IOperationGQL } from '@core/domain/interfaces';
import { SegmentedZoneCapabilitiesFacade } from '../../facades';

@Injectable({
  providedIn: 'root',
})
export class CapabilitiesFiltersProvider {
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(CapabilitiesStore);
  private readonly notify = inject(NotifyService);
  private readonly segmentedCapabilitiesFacade = inject(
    SegmentedZoneCapabilitiesFacade,
  );

  readonly formGroup = this.fb.group({
    filial: this.fb.control('', [Validators.required]),
    operation: this.fb.control('', [Validators.required]),
    date: this.fb.control<DateTime>(null, [Validators.required]),
  });

  private readonly values$ = toSignal(this.formGroup.valueChanges);

  private readonly operations = signal<IOperationGQL[]>([]);

  readonly isFetchingList = computed(() =>
    this.segmentedCapabilitiesFacade.listCapabilities.running(),
  );

  readonly hasFilterChanges$ = computed(() => {
    const filters = this.store.filters();
    const values = this.values$();

    return [
      filters?.date.toFormat('yyyy-MM-dd') !==
        values?.date.toFormat('yyyy-MM-dd'),
      filters?.operation !== values?.operation,
      filters?.filial !== values?.filial,
    ].some(Boolean);
  });

  readonly canEditFilials$ = computed(() => {
    const filters = this.formGroup.value;

    const canEdit = DateTimeAdapter.isGreatherThanToday(
      filters?.date?.toSQLDate(),
    );

    return (
      !!this.store.capabilitiesFilial$().filial_operation_capability_id &&
      canEdit
    );
  });

  readonly canEditBags$ = computed(() => {
    const filters = this.formGroup.value;

    const canEdit = DateTimeAdapter.isGreatherThanToday(
      filters?.date?.toSQLDate(),
    );

    return !!this.store.bagsCapabilities()?.length && canEdit;
  });

  readonly segmentedBagsLists$ = computed(() =>
    this.store.segmentedBagsLists$(),
  );

  readonly selectedOperation$ = computed(() => {
    const operationCode = this.values$().operation;
    const operation = this.operations()?.find((o) => o.id === operationCode);

    return operation;
  });

  readonly storeFilters = computed(() => this.store.filters());

  initValues(): void {
    this.formGroup.setValue(this.store.filters());
  }

  setOperations(operations: IOperationGQL[]) {
    this.operations.set(operations);
  }

  getControl(field: string) {
    return this.formGroup.get(field);
  }

  async retrieveCapabilities() {
    const filters = this.formGroup.value as TCapabilitiesFiltersStore;
    const result =
      await this.segmentedCapabilitiesFacade.listCapabilities.execute(filters);

    if (result.isOk === false) {
      this.notify.error('Espera!!', result.error.message);
    }
  }
}
